// src/ui/Button.tsx (T1): buttons and link-buttons (design system A: 8px corners, Onest, Title Case labels; the
// primary button is the builder's accent).
// A `href` renders a link: same-origin paths route client-side through the router's Link; tel:, mailto: and
// absolute URLs render a plain <a>. Root classes: hb-button / hb-icon-button / hb-link-button / hb-link.
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ComponentChildren } from "preact";
import { Link } from "../app/router";
import { cx } from "./cx";
import { Icon, type IconName } from "./Icon";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "on-navy" | "line-navy";

type Common = {
  variant?: ButtonVariant;
  size?: "md" | "lg";
  block?: boolean;
  icon?: IconName;
  iconAfter?: IconName;
  /** Shows a busy state and blocks clicks; the label stays so the button keeps its width. */
  busy?: boolean;
  class?: string;
  children?: ComponentChildren;
};
type AsButton = Common & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "class" | "className" | "size" | "icon"> & { href?: undefined };
type AsLink = Common & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "class" | "className" | "size" | "icon" | "href"> & { href: string; replace?: boolean };
export type ButtonProps = AsButton | AsLink;

/** True for a same-origin path the SPA router handles ("/p/..."), false for tel:, mailto:, http(s) and "//". */
export function isAppPath(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//");
}

export function Button(props: ButtonProps) {
  const { variant = "primary", size = "md", block, icon, iconAfter, busy, class: extra, children, ...rest } = props;
  const cls = cx("hb-button", "btn", `btn-${variant}`, size === "lg" && "btn-lg", block && "btn-block", extra);
  const inner = (
    <>
      {icon ? <Icon name={icon} /> : null}
      {children}
      {iconAfter ? <Icon name={iconAfter} /> : null}
    </>
  );
  if (rest.href !== undefined) {
    const { href, replace, ...a } = rest as AsLink;
    if (isAppPath(href)) {
      return (
        <Link {...a} to={href} replace={replace} class={cls}>
          {inner}
        </Link>
      );
    }
    return (
      <a {...a} href={href} class={cls}>
        {inner}
      </a>
    );
  }
  const b = rest as Omit<AsButton, keyof Common>;
  return (
    <button
      type="button"
      {...b}
      class={cls}
      aria-busy={busy ? "true" : undefined}
      disabled={b.disabled}
      onClick={busy ? (e) => e.preventDefault() : b.onClick}
      data-busy={busy ? "" : undefined}
    >
      {inner}
    </button>
  );
}

type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "class" | "className" | "icon" | "size"> & {
  icon: IconName;
  /** The accessible name (there is no visible text). */
  label: string;
  size?: "sm" | "md";
  class?: string;
};
/** A square icon-only button: 40px (44px on touch), `size="sm"` 28px. */
export function IconButton(props: IconButtonProps) {
  const { icon, label, size = "md", class: extra, ...rest } = props;
  return (
    <button type="button" {...rest} class={cx("hb-icon-button", "icon-btn", size === "sm" && "icon-btn-sm", extra)} aria-label={label}>
      <Icon name={icon} size={size === "sm" ? "sm" : undefined} />
    </button>
  );
}

type LinkButtonProps = Common & { href?: string; onClick?: (e: MouseEvent) => void; type?: "button" | "submit" };
/** A standalone text action with a 44px tap target ("Suggest Another Time", "All Activity"). */
export function LinkButton(props: LinkButtonProps) {
  const { icon, iconAfter, class: extra, children, href, onClick, type } = props;
  const cls = cx("hb-link-button", "link-btn", extra);
  const inner = (
    <>
      {icon ? <Icon name={icon} size="sm" /> : null}
      {children}
      {iconAfter ? <Icon name={iconAfter} size="sm" /> : null}
    </>
  );
  if (href !== undefined) {
    return isAppPath(href) ? (
      <Link to={href} class={cls} onClick={onClick}>
        {inner}
      </Link>
    ) : (
      <a href={href} class={cls} onClick={onClick}>
        {inner}
      </a>
    );
  }
  return (
    <button type={type ?? "button"} class={cls} onClick={onClick}>
      {inner}
    </button>
  );
}

/** An inline text link (navy, soft underline). */
export function TextLink(props: { href: string; class?: string; children?: ComponentChildren; target?: string; rel?: string }) {
  const { href, class: extra, children, ...rest } = props;
  const cls = cx("hb-link", "link", extra);
  return isAppPath(href) ? (
    <Link to={href} class={cls} {...rest}>
      {children}
    </Link>
  ) : (
    <a href={href} class={cls} {...rest}>
      {children}
    </a>
  );
}
